<template>
  <div>
    <div class="d-flex mb-3 align-items-end">
      <h1 class="h4 primary--text">
        {{ $localT('add') }}
      </h1>
      <div class="ml-auto">
        <v-btn
          class="mr-4"
          outlined
          large
          :disabled="disabled"
          @click="previewModal = true"
        >
          {{ $t('common.preview') }}
        </v-btn>
        <v-btn
          class="mr-4"
          outlined
          large
          :disabled="disabled"
          :loading="loading"
          @click="save(false)"
        >
          {{ $t('common.save') }}
        </v-btn>
        <v-btn
          color="primary"
          outlined
          large
          :disabled="disabled"
          :loading="loading"
          @click="save(true)"
        >
          {{ news.isPublished ? $t('common.unpublish') : $t('common.publish') }}
        </v-btn>
      </div>
    </div>
    <v-card elevation="0">
      <v-card-text>
        <div class="news-editor pb-6">
          <template v-if="isHelp">
            <div class="black--text mb-2">
              Select a type
            </div>
            <v-select
              v-model="helpType"
              :items="helpItems"
              outlined
              rows="3"
            />
          </template>
          <div class="black--text mb-2">
            {{ $localT('title') }}
          </div>
          <v-textarea
            v-model="news.subject"
            outlined
            :rows="isHelp ? 1 : 3"
          />
          <template v-if="isHelp">
            <div class="black--text mb-2">
              Lead
            </div>
            <v-text-field
              v-model="news.lead"
              outlined
            />
          </template>
          <template v-if="isTop10 || isHelp && helpType === 'link'">
            <div class="black--text mb-2">
              {{ $localT('link') }}
            </div>
            <v-textarea
              v-model="news.link"
              outlined
              :rows="isHelp ? 1 : 3"
            />
          </template>
          <template v-if="isNews">
            <div class="black--text mb-2">
              {{ $t('views.news.news_lead') }}
            </div>
            <v-textarea
              v-model="news.lead"
              outlined
              rows="3"
            />
          </template>
          <template v-if="isTop10 || isNews || isSpecialOffer || isHelp && helpType === 'image'">
            <input
              v-show="false"
              ref="file"
              type="file"
              multiple
              accept="image/x-png,image/jpeg,image/webp"
              @change="uploadFile"
            >
            <v-btn
              type="button"
              outlined
              large
              :loading="previewLoading"
              class="mb-4"
              @click="setFile"
            >
              <v-icon left>
                mdi-plus
              </v-icon>
              {{ $t('views.news.select_preview_image') }}
            </v-btn>
            <div
              v-if="news.image"
              class="mb-4"
            >
              {{ news.image.fileName }}
            </div>
          </template>
          <template
            v-if="!isTop10 && !isHelp || isHelp && helpType === 'content'"
          >
            <div class="black--text mb-2">
              {{ $localT('content') }}
            </div>
            <vue-editor
              v-model="news.content"
              class="mb-6"
            />
          </template>
          <v-row v-if="isSpecialOffer">
            <v-col cols="3">
              <div class="input-block input-block_white">
                <label class="input-block__label black--text">{{ $t('views.news.start_date') }}</label>
                <date-picker
                  key="news-start-date"
                  v-model="news.startDate"
                  :config="getStartDateConfig"
                  output="Z"
                  hide-details
                />
              </div>
            </v-col>
            <v-col cols="3">
              <div class="input-block input-block_white">
                <label class="input-block__label black--text">{{ $t('views.news.end_date') }}</label>
                <date-picker
                  key="news-end-date"
                  v-model="news.endDate"
                  :config="getEndDateConfig"
                  output="Z"
                  hide-details
                />
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
    <preview-modal
      v-model="previewModal"
      :news="typeParams"
    />
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor';
import { v4 as uuidv4 } from 'uuid';
import _pick from 'lodash.pick';
import _omit from 'lodash.omit';
import PreviewModal from '@/components/news/PreviewModal';
import DatePicker from '@/components/common/DatePicker';
import { contentTypes, hyphense } from '@/utils/enums';

export default {
  name: 'NewsEditor',
  components: {
    VueEditor,
    PreviewModal,
    DatePicker,
  },
  props: {
    id: String,
    parentId: String,
    type: String,
  },
  data: () => ({
    helpItems: ['node', 'link', 'image', 'content'],
    helpType: '',
    news: {
      subject: '',
      lead: '',
      link: '',
      content: '',
      image: null,
      isPublished: false,
      startDate: '',
      endDate: '',
    },
    loading: false,
    previewModal: false,
    previewLoading: false,
  }),
  computed: {
    disabled() {
      if (this.isNews) {
        return !this.news.subject || !this.news.content || !this.news.lead || !this.news.image;
      }

      if (this.isSpecialOffer) {
        return !this.news.subject || !this.news.content || !this.news.startDate || !this.news.endDate;
      }

      if (this.isTop10) {
        return !this.news.link || !this.news.image;
      }

      if (this.isHelp) {
        return !this.news.subject || !this.news.lead;
      }
      return true;
    },
    isNews() {
      return this.type === hyphense(contentTypes.ref.news);
    },
    isSpecialOffer() {
      return this.type === hyphense(contentTypes.ref.specialOffer);
    },
    isTop10() {
      return this.type === hyphense(contentTypes.ref.top);
    },
    isHelp() {
      return this.type === hyphense(contentTypes.ref.help);
    },

    typeParams() {
      let typeParams;

      if (this.isTop10) {
        typeParams = _pick(this.news, ['link', 'image', 'subject']);
      } else if (this.isHelp) {
        if (this.helpType === 'node') {
          typeParams = _pick(this.news, ['subject', 'lead']);
        }
        if (this.helpType === 'link') {
          typeParams = _pick(this.news, ['subject', 'lead', 'link']);
        }
        if (this.helpType === 'image') {
          typeParams = _pick(this.news, ['subject', 'lead', 'image']);
        }
        if (this.helpType === 'content') {
          typeParams = _pick(this.news, ['subject', 'lead', 'content']);
        }

        // console.log('!', this, typeParams);
        if (typeParams && this.parentId) {
          typeParams.parent = this.parentId;
        }
        // if (this.parentId) {
        //   typeParams.parentId = this.parentId;
        // }
      } else if (this.isNews) {
        typeParams = _omit(this.news, ['startDate', 'endDate', 'link']);
      } else if (this.isSpecialOffer) {
        typeParams = _omit(this.news, ['link']);
      }
      return typeParams || {
        subject: '',
        lead: '',
        link: '',
        content: '',
        image: null,
        isPublished: false,
        startDate: '',
        endDate: '',
      };
    },

    getStartDateConfig() {
      const now = this.$moment();
      const lastDate = this.$moment(now).add(99, 'year');
      const { startDate } = this.news;

      return {
        enable: [
          ...(startDate ? [startDate] : []),
          { from: now.toISOString(), to: lastDate.toISOString() },
        ],
      };
    },

    getEndDateConfig() {
      const now = this.$moment();
      const lastDate = this.$moment(now).add(99, 'year');
      const { startDate, endDate } = this.news;

      const startMoment = this.$moment(startDate);
      const minDate = (startMoment < now) ? now : startMoment.add(1, 'day');

      return {
        enable: [
          ...(endDate ? [endDate] : []),
          { from: minDate.toISOString(), to: lastDate.toISOString() },
        ],
      };
    },
  },
  watch: {
    'news.startDate': {
      handler(day) {
        if (this.news.endDate && this.$moment(this.news.endDate) < this.$moment(day)) {
          this.$set(this.news, 'endDate', this.$moment(day).add(1, 'days').toISOString());
        }
      },
    },
  },
  created() {
    if (this.id) {
      this.getNews();
    }
  },
  methods: {
    $localT(text) {
      return this.$t(`views.${this.type}.${text}`);
    },

    async save(isPublished) {
      this.news.isPublished = isPublished;

      const { id } = this.news;

      const params = {
        ...this.typeParams,
        contentType: contentTypes.hypRef[this.type],
        isPublished,
      };

      try {
        this.loading = true;

        if (id) {
          delete params.contentType;
          delete params.date;

          await this.$http.put(`/content/${id}`, params);
        } else {
          const { data } = await this.$http.post('content', params);
          this.$router.push(`/content/page/${data.id}/${this.type}`);
        }
      } finally {
        this.loading = false;
      }
    },
    async uploadFile() {
      const { files } = this.$refs.file;
      const file = files[0];
      const fileId = uuidv4();
      const { name, type, size } = file;

      const reader = new FileReader();
      reader.onload = async () => {
        this.previewLoading = true;
        await this.$http.post('containers/file', reader.result, {
          headers: {
            container: 'content-images',
            file: fileId,
            'Content-Type': type,
          },
        });

        this.news.image = {
          fileName: name,
          fileId,
          filePath: URL.createObjectURL(file),
          size,
        };

        this.previewLoading = false;
      };
      reader.readAsArrayBuffer(file);
    },
    async setFile() {
      this.$refs.file.value = null;
      this.$refs.file.click();
    },
    async getNews() {
      this.loading = true;
      let params = {};
      if (!this.isHelp || this.helpType === 'image') {
        params = {
          populate: 'image',
        };
      }
      const { data } = await this.$http.get(`/content/${this.id}`, { params }).finally(() => {
        this.loading = false;
      });
      if (this.isHelp) {
        this.setHelpType(data);
      }
      this.news = data;
    },
    setHelpType(help) {
      if (help.content) {
        this.helpType = 'content';
        return;
      }
      if (help.image) {
        this.helpType = 'image';
        return;
      }
      if (help.link) {
        this.helpType = 'link';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.news-editor {
  max-width: 1000px;
}
</style>
